import { makeAutoObservable } from 'mobx';
import { enableStaticRendering } from 'mobx-react-lite';
import { option } from '../../components/CustomFieldComponent';
import { TicketFormValues } from '../../components/GrispiTicketForm';
import { storageItemKeyFactoryForCustomizedOptions } from '../../utils/forms/formFieldsHelper.options';
import { isSSR } from '../../utils/helpers';
import { addAppPrefixToStorageKey } from '../../utils/storageHelpers';

enableStaticRendering(isSSR());

export interface ICustomizedOptions {
    fieldKey: string;
    options: option[];
}

export class TicketFormUtilStore {
    isUploadingAttachment: boolean;
    resetFileListTriggered: boolean;
    initialValues: Record<string, TicketFormValues>;
    initialCreateTicketValues: TicketFormValues;
    viewOption: '0' | '1' | '2'; // 0: OrganizationViewContainer, 1: RequesterViewContainer, 2: EditTicketFieldsContainer|CreateTicketFieldsContainer
    constructor() {
        this.isUploadingAttachment = false;
        this.resetFileListTriggered = false;
        this.viewOption = '2';
        this.initialValues = {};

        // Call it here
        makeAutoObservable(this);
    }

    resetFileList = (state: boolean): void => {
        this.resetFileListTriggered = state;
    };

    setIsUploadingAttachment = (state: boolean): void => {
        this.isUploadingAttachment = state;
    };

    initAllCustomizedOptionsOnStore = (
        formName: string,
        customizedOptionsListOnStorage: ICustomizedOptions[]
    ): void => {
        customizedOptionsListOnStorage.forEach((item) => {
            const storeKey = storageItemKeyFactoryForCustomizedOptions(formName)(item?.fieldKey);

            if (!storeKey) {
                // console.debug(
                //     'TicketFormUtilStore initAllCustomizedOptionsOnStore: storeKey is empty, will not set customized options on store'
                // );
            }
            if (storeKey) {
                this[storeKey] = item?.options;
            }
        });
    };

    /**
     *
     * TODO cagri: add information to the formReset param
     * @param formReset : flag indicating whether the input-options should be reset or not
     */
    initCustomizedOptionsOnStore = (formName: string, fieldKey: string, options: option[], formReset = false): void => {
        const storeKey = storageItemKeyFactoryForCustomizedOptions(formName)(fieldKey);

        if (!storeKey) {
            // console.debug(
            //     'TicketFormUtilStore initCustomizedOptionsOnStore: storeKey is empty, will not set customized options on store'
            // );
        }

        if (formReset) {
            this[storeKey] = { options, formReset };
            return;
        }

        this[storeKey] = options;
    };

    resetFieldOptions = (formName: string): void => {
        const fieldKeyList = Object.keys(this).filter((key) => key.includes(formName));

        fieldKeyList.forEach((fieldKey) => {
            delete this[fieldKey];
        });
    };

    setFieldsOptions = (formName: string, fieldKey: string, options: option[]): void => {
        const storeKey = storageItemKeyFactoryForCustomizedOptions(formName)(fieldKey);

        this[storeKey] = options;
    };

    resetInitialValues = (formName: string): void => {
        const storeKey = addAppPrefixToStorageKey(formName);

        delete this.initialValues[storeKey];
    };
    setInitialValues = (formName: string, initialValues: TicketFormValues): void => {
        const storeKey = addAppPrefixToStorageKey(formName);

        this.initialValues[storeKey] = initialValues;
    };

    setInitialCreateTicketValues = (initialValues: TicketFormValues): void => {
        this.initialCreateTicketValues = initialValues;
    };
    getInitialFormValues = (formName: string): TicketFormValues => {
        const storeKey = addAppPrefixToStorageKey(formName);

        return this.initialValues[storeKey];
    };

    getInitialCreateTicketValues = (): TicketFormValues => {
        return this?.initialCreateTicketValues ?? ({} as TicketFormValues);
    };

    setViewOption = (viewOption: '0' | '1' | '2'): void => {
        this.viewOption = viewOption;
    };
}
